import React, { useCallback, useContext, useEffect, useState, useRef } from 'react'
import { NavigationContainer, DefaultTheme, DarkTheme, useNavigationContainerRef } from '@react-navigation/native'
import { buildLinkingConfig } from './linkingConfig'
import { auth, logout, rootDb } from './Firebase/firebase'
import AuthStack from './AuthStack'
import AppStack from './AppStack'
import BundleGate from '../components/BundleGate'
import { UserContext } from './UserProvider'
import Spinner from '../components/Spinner'
import * as Linking from 'expo-linking'
import * as Sentry from "@sentry/react-native"
import { logFBAnalyticsScreenView, setFBAnalyticsUserId } from './Firebase/firebaseAnalytics'
import { useAtom, useSetAtom, useAtomValue } from 'jotai'
import { dbAtom, userAtom, userLocaleAtom, signupInProgressAtom, resolvedThemeAtom } from './AtomsProvider'
import { useTranslation } from 'react-i18next'
import { setDefaultOptions } from "date-fns"
import { enUS, ru } from 'date-fns/locale'
import { PostHogProvider, usePostHog } from 'posthog-react-native'
import { StatusBar } from 'expo-status-bar'
import { Platform } from 'react-native'
import AnalyticsIdentityBridge from './AnalyticsIdentityBridge'
import GrowthAttributionBridge from './GrowthAttributionBridge'
import { AppReviewSessionBridge } from './AppReview'
import { buildProvenanceProperties } from '../utils/buildProvenance'
import { captureConnectAiRequest, connectAiRedirectStep, consentStackAbsent, readPendingConnectAiRequest } from './AiAssistants/connectAiRequest'

// ATL-1694: Claude / ChatGPT send an admin to `/connect-ai?request=<id>`. Read it NOW, at module
// load, before NavigationContainer mounts: a signed-out visitor's URL is rewritten to the auth
// stack's path before any screen could see it. The NavigationContainer below reopens the consent
// screen after sign-in (openPendingConnectAi).
if (Platform.OS === 'web') captureConnectAiRequest()

// ATL-531: static build provenance registers ONCE per JS process, at PostHogProvider mount (not
// at identify), so pre-login events and the first events after an OTA reload carry the CURRENT
// bundle's identity (PostHog persists super properties to disk — without this, early events
// inherit the previous bundle's values). The org-dependent gate state (dual_write_eligible)
// registers at identify in AppStack. update_id pins the JS bundle that actually carries (or
// lacks) the dual-write code — the field that was missing when BMgroup silently dropped 14/16
// sales from Supabase (2026-06-05).
// The payload itself lives in utils/buildProvenance.js because `postHog.reset()` — which
// ASO-AN-001 now performs on sign-out — WIPES super properties, and this effect (keyed on the
// PostHog client, which survives a logout) would never re-run to restore them.
const BuildProvenanceRegistrar = () => {
  const postHog = usePostHog()
  useEffect(() => {
    void postHog.register(buildProvenanceProperties())
  }, [postHog])
  return null
}

export default function Routes() {
  const [context, setContext] = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(true)
  const prefix = Linking.createURL('/')
  const [user, setUser] = useAtom(userAtom)
  const setDb = useSetAtom(dbAtom)
  const [userLocale, setUserLocale] = useAtom(userLocaleAtom)
  const signupInProgress = useAtomValue(signupInProgressAtom)
  const signupInProgressRef = useRef(signupInProgress)
  const { i18n } = useTranslation()
  const resolvedTheme = useAtomValue(resolvedThemeAtom)
  const navigationTheme = resolvedTheme === 'dark' ? {
    ...DarkTheme,
    colors: {
      ...DarkTheme.colors,
      background: '#141414',  // matches Eva bg-3
      card: '#2C2C2E',        // matches Eva bg-1 (surfaces)
      border: '#48484A',      // matches Eva basic-700
      text: '#F2F2F7',        // matches Eva basic-200
    },
  } : DefaultTheme

  // ATL-1694: open the consent screen for a request captured before sign-in. Decided from the
  // container's COMMITTED state (onReady / onStateChange): a navigate from Home's mount effect is
  // overwritten by the signed-in stack's own initial commit — see connectAiRedirectStep. Each
  // request is opened at most once per signed-in session, so leaving the screen never reopens it;
  // a sign-out starts over (consentStackAbsent), so an admin signing in after a non-admin is brought
  // to the request the non-admin could not decide.
  const navigationRef = useNavigationContainerRef()
  const handledConnectAiRequestsRef = useRef(new Set())
  const openPendingConnectAi = useCallback(() => {
    if (Platform.OS !== 'web' || !navigationRef.isReady()) return
    const rootState = navigationRef.getRootState()
    if (consentStackAbsent(rootState)) handledConnectAiRequestsRef.current.clear()
    const pending = readPendingConnectAiRequest()
    const step = connectAiRedirectStep(pending, rootState, handledConnectAiRequestsRef.current)
    if (step === 'none' || pending === null) return
    handledConnectAiRequestsRef.current.add(pending)
    if (step === 'open') navigationRef.navigate('ConnectAi', { request: pending })
  }, [navigationRef])

  // Keep ref in sync with atom value for use in callbacks
  useEffect(() => {
    signupInProgressRef.current = signupInProgress
  }, [signupInProgress])

  // Single source of truth for locale side effects: keep date-fns' global
  // locale (setDefaultOptions) and the i18n language synced to the persisted
  // userLocaleAtom. This runs on first mount, when the atom hydrates from
  // AsyncStorage, when the Firestore user.locale loads (setUserDetails), and on
  // a manual change in Profile — all through the same atom, so the two never
  // diverge. Without this, date-fns stayed on its built-in enUS default at
  // startup (nothing applied the persisted locale to it), so invoices/receipts
  // formatted with `format(date, 'd MMMM y')` printed English month names even
  // when the UI was Russian — until the user re-saved the language in Profile.
  useEffect(() => {
    setDefaultOptions({ locale: userLocale === 'ru' ? ru : enUS })
    if (i18n.language !== userLocale) {
      void i18n.changeLanguage(userLocale)
    }
  }, [userLocale, i18n])
  // ATL-632 / QARASAY-KASSA-27D: the linking map and its two path<->state hooks live in
  // ./linkingConfig so their rules are testable outside this component. Do NOT inline them
  // again — read the docblock there before adding a screen.
  const linking = buildLinkingConfig(prefix)

  const setUserDetails = async authUser => {
    // Skip user data loading during signup (Supabase user may not exist yet)
    if (signupInProgressRef.current) {
      console.log('Signup in progress, skipping user data loading')
      return
    }
    try {
      if (authUser) {
        await rootDb.collection('users').where('uid', '==', authUser.uid).get().then(function(querySnapshot) {
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0]
            var user = doc.data()
            user.id = doc.id
            user.ref = doc.ref
            user.text = user.name
            user.title = user.name
            setContext(context => ({
              ...context, 
              user: {...authUser, ...user}, 
              db: rootDb.collection('organizations').doc(user.organizationId)
            }))
            setUser({...authUser, ...user})
            setDb(rootDb.collection('organizations').doc(user.organizationId))
            if (user.locale) {
              // Drive the persisted atom; the locale effect above applies the
              // i18n language and date-fns global locale from it.
              setUserLocale(user.locale)
            }
          } else {
            alert('User was not assigned necessary properties')
            logout()
          }
        })
      } else {
        setFBAnalyticsUserId(null)
        setContext(context => ({...context, user: null}))
        setUser(null)
      }
      setIsLoading(false)
    } catch (error) {
      Sentry.captureException(error)
      console.log(error)
    }
  }

  // Get the current screen from the navigation state
  function getActiveRouteName(navigationState) {
    if (!navigationState || !Array.isArray(navigationState.routes)) {
      return null
    }
    const index = typeof navigationState.index === 'number' ? navigationState.index : 0
    const route = navigationState.routes[index]
    if (!route) {
      return null
    }
    // Parse the nested navigators
    if (route.state && route.state.routes) {
      return getActiveRouteName(route.state)
    }
    if (route.routes) return getActiveRouteName(route)
    return route.name || route.routeName || null
  }


  useEffect(() => {
    // unsubscribe listeners on unmount
    const unsubscribeFirebaseUser = auth.onAuthStateChanged(setUserDetails)
    return () => {
      unsubscribeFirebaseUser()
    }
  }, [])

  // After signup completes, load user data
  useEffect(() => {
    if (!signupInProgress && auth.currentUser && !user) {
      console.log('Signup completed, loading user data')
      setUserDetails(auth.currentUser)
    }
  }, [signupInProgress])

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <StatusBar style={resolvedTheme === 'dark' ? 'light' : 'dark'} />
      <NavigationContainer linking={linking} theme={navigationTheme}
        ref={navigationRef}
        onReady={openPendingConnectAi}
        onStateChange={openPendingConnectAi}
        onNavigationStateChange={(prevState, currentState) => {
          const currentScreen = getActiveRouteName(currentState);
          const prevScreen = getActiveRouteName(prevState);
          if (prevScreen !== currentScreen) {
            // Update Firebase with the name of your screen
            logFBAnalyticsScreenView({
              screen_name: currentScreen,
              screen_class: currentScreen,
            })
          }
        }}
      >
        <PostHogProvider
          apiKey="phc_TWqOxmMAbnQ8daiisaYO3CqbknzUFqJKQi9OJoq74C9"
          options={{
            enableSessionReplay: true,
          }}
        >
          <BuildProvenanceRegistrar />
          {/* ASO-AN-001: identity is bound to Firebase Auth here, ABOVE the AppStack/AuthStack
              branch, so it is established for signed-in and signing-up users alike — the signup
              event is captured from AuthStack and used to be permanently anonymous. */}
          <AnalyticsIdentityBridge />
          {/* ASO-ATTR-001: Apple Ads attribution + `app_first_opened`. Mounted here — below the
              `isLoading` spinner return, above the AppStack/AuthStack branch — because an Apple
              Ads install is measured from BEFORE signup, and because this is the first point at
              which a screen the user can act on has been rendered. */}
          <GrowthAttributionBridge />
          {/* ASO-RATING-2026-001: counts sessions so the App Store rating request is never made in a
              device's first session. Below the spinner for the same reason as the bridge above, and
              WRAPPING the stacks because a session's activity is a touch anywhere in them. */}
          {/* ATL-1452: the bundle gate — ONCE per process, above the signed-in/signed-out branch,
              so "launch" is the process start and a sign-out/sign-in never remounts it. */}
          <BundleGate>
            <AppReviewSessionBridge>
              {user ? <AppStack /> : <AuthStack />}
            </AppReviewSessionBridge>
          </BundleGate>
        </PostHogProvider>
      </NavigationContainer>
    </>
  )
}
